import dateUtils from '@/common/dateUtils';
import dataUtils from '@/common/dataUtils';
import utils from '@/common/utils';
import { DAY_IN_MS, HOURS_IN_MS } from '@/common/constants';

const ONE_DAY_MS = 60 * 60 * 24 * 1000;

export default {
  /**
   * Tests if an element can be accessed when the user has userGroups and
   * the element is restricted one of the groups listed in elementRestrictedToGroups
   * @param userGroups
   * @param elementRestrictedToGroups
   * @returns {boolean}
   */
  isUserPermittedWhenRestrictedToGroups(userGroups, elementRestrictedToGroups) {
    if (!userGroups) {
      userGroups = [];
    }
    if (!elementRestrictedToGroups) {
      elementRestrictedToGroups = [];
    }
    if (elementRestrictedToGroups.length == 0) return true;
    if (userGroups.length == 0 && elementRestrictedToGroups.length > 0) return false;
    let groupInRequiredGroups = elementRestrictedToGroups.find((group) => userGroups.indexOf(group) > -1);
    return !!groupInRequiredGroups;
  },

  markAbsenceRow(absence, marker) {
    let el = document.getElementById(`abs_${absence.id}`);
    if (el) {
      el.classList.add(marker);
    }
  },
  removeAbsenceRowMarker(absence, marker) {
    let el = document.getElementById(`abs_${absence.id}`);
    if (el) {
      el.classList.remove(marker);
    }
  },
  clearUpdateAndCreatedMarks(item, seconds) {
    setTimeout(() => {
      let items;
      if (Array.isArray(item)) {
        items = item;
      } else {
        items = [item];
      }
      items.forEach((i) => {
        console.debug('clear _updated and _created state on item', i);
        i._updated = false;
        i._created = false;
      });
    }, seconds * 1000);
  },
  /**
   * Filter an array of items
   * @param filterDate
   * @param items
   * @param deletedOnly
   * @param ignoreDeleted
   * @param dateAttribute
   * @param dateFilterEnabled
   * @returns {*}
   */
  filterItems(filterDate, items, deletedOnly = false, ignoreDeleted = true, dateAttribute = 'date', dateFilterEnabled = true) {
    if (!items) return [];

    let date = new Date(filterDate.getTime());
    date.setHours(0);
    date.setMinutes(0);
    let datePlusOneDay = new Date(date.getTime() + ONE_DAY_MS);
    const dateMs = date.getTime();
    const datePlusOneDayMs = datePlusOneDay.getTime();

    let filtered = items.filter((item) => {
      if (!item) return false;
      if (ignoreDeleted && item.deleted === true) return false;
      if (deletedOnly && item.deleted === false) return false;

      if (dateFilterEnabled) {
        // use recordDate if available, otherwise use the provided dateAttribute,e.g. dutyFrom
        // dutyFrom is not always reliable, therefore a new recordDate has been introduced
        console.debug(`recordDate = ${item.recordDate}`);
        let startMs = Date.parse(item.recordDate ? item.recordDate : item[dateAttribute]);

        return startMs >= dateMs && startMs < datePlusOneDayMs;
      }
      return true;
    });
    return filtered;
  },
  buildItemsByDate(items, dateAttribute = 'date') {
    return dataUtils.buildArrayMapById(items, (item) => {
      if (!item) return null;
      let itemDate = item.recordDate ? item.recordDate : item[dateAttribute];
      let dateIsoStr = itemDate ? itemDate : item.dutyDateFallback;
      return this.buildDateId(dateIsoStr);
    });
  },
  buildDateIdFromDate(date) {
    let month = `${date.getMonth()}`.padStart(2, '0');
    let day = `${date.getDate()}`.padStart(2, '0');
    return `${date.getFullYear()}${month}${day}`;
  },
  buildDateId(dateIsoStr) {
    if (dateIsoStr) {
      let date = new Date(Date.parse(dateIsoStr));
      return this.buildDateIdFromDate(date);
    }
    return null;
  },
  sortById(items) {
    return items.slice().sort((a, b) => {
      return a.id - b.id < 0 ? -1 : 1;
    });
  },
  getDateFrom(currentDate, HHMMStringOrIsoString) {
    try {
      let millis = Date.parse(HHMMStringOrIsoString);
      let date;
      if (millis) {
        date = new Date(millis);
      } else {
        date = HHMMStringOrIsoString ? dateUtils.dateHHMMToDate(currentDate, HHMMStringOrIsoString) : null;
      }
      return date;
    } catch (err) {
      console.warn(`getDateFrom(${currentDate}, ${HHMMStringOrIsoString}) failed`, err);
      return null;
    }
  },
  getDateIsoStringFromDateAndHHMM(date, HHMM = '00:00') {
    console.debug('getDateIsoStringFrom', date, 'HHMMStringOrIsoString', HHMM);
    let adaptedDate = new Date(date.getTime());
    const HHMMParts = HHMM.split(':');
    adaptedDate.setHours(Number(HHMMParts[0]));
    adaptedDate.setMinutes(Number(HHMMParts[1]));
    if (adaptedDate.getMonth) return adaptedDate.toISOString();
    return adaptedDate;
  },
  getDateIsoStringFrom(date, HHMM = '00:00') {
    let adaptedDate = this.getDateFrom(date, HHMM);
    if (adaptedDate.getMonth) return adaptedDate.toISOString();
    return adaptedDate;
  },
  /**
   * Calculate fahrdienst hours of relevant duties and sum up
   * @param absences
   * @param fromDate
   * @param toDate
   * @param zueri9Clients
   * @param zueri9Reasons
   */
  calculateFahrdienstHour(absences, fromDate, toDate, zueri9Clients, zueri9Reasons) {
    // evaluate foreignKeys (id's) for zueri9Clients and reasons taken into account
    let fahrdienstId = zueri9Clients.find((c) => c.short === 'F').id;
    let tramId = zueri9Reasons.find((c) => c.short === 'T').id;
    let busId = zueri9Reasons.find((c) => c.short === 'B').id;
    let trolleyId = zueri9Reasons.find((c) => c.short === 'TB').id;

    let allowedReasons = [tramId, busId, trolleyId];

    let applicableHoursPerDayInCurrentMonth = {};
    let itemsPerDay = {};
    absences.forEach((a) => {
      const isNotDeleted = !a.deleted;
      const isAssignedToZueri9 = a.assignedTo === 'zueri9';
      if (!isAssignedToZueri9) return;

      const isFahrdienst = a.zueri9ClientId === fahrdienstId; // Fahrdienst
      const isAllowedReason = allowedReasons.includes(a.zueri9ReasonId); // Tram, Bus, Trolley
      const isZueri9StateCompleted = a.zueri9State === 3; //consider completed items only
      if (isNotDeleted && isZueri9StateCompleted && isAssignedToZueri9 && isFahrdienst && isAllowedReason) {
        if (a.z9DutyUntil && a.z9DutyFrom) {
          let date = new Date(a.recordDate);
          let dateDay = date.getDate();

          if (this.isBetweenIncludingEnd(date, fromDate, toDate)) {
            let timeMs = Date.parse(a.z9DutyUntil) - Date.parse(a.z9DutyFrom);
            let hours = timeMs / HOURS_IN_MS;
            if (hours < 0) hours = 24 + hours;
            let dayTotal = applicableHoursPerDayInCurrentMonth[date.getDate()] || 0;
            applicableHoursPerDayInCurrentMonth[date.getDate()] = dayTotal + hours;

            if (!itemsPerDay[dateDay]) itemsPerDay[dateDay] = [];
            let records = itemsPerDay[dateDay];
            records.push(a);

            if (dateDay === 11) {
              console.debug(
                `item ${a.id} considered item with recordDate=${a.recordDate}, from-to ${a.z9DutyFrom} - ${a.z9DutyUntil}, zueri9ClientId=${a.zueri9ClientId} (fahrdienstId=${fahrdienstId}) zuer9ReasonId=${a.zueri9ReasonId} (allowed=${allowedReasons}) a.zueri9Stat=${a.zueri9State} should be 3`
              );
            }
          }
        }
      }
    });
    return { applicableHoursPerDayInCurrentMonth, itemsPerDay };
  },
  /**
   * Calculate the fahrdienst statistics of the current month until the currentWorksheetDate
   * @param absences - list of absences
   * @param currentWorksheetDate - the date of the current view
   * @param zueri9Clients - lookup table (Züri 9 'für Wen')
   * @param zueri9Reasons - lookup table (Züri 9 'Was')
   * @param zueri9FahrstundenTagesRichtwert - Richtwert
   * @returns {{calculatedForDate, monthTotal: number, delta: number, applicableHoursPerDayInCurrentMonth: {}, hoursAtContextDate: number}}
   */
  calculateZueri9FahrdienstStats({ absences, currentWorksheetDate, zueri9Clients, zueri9Reasons, zueri9FahrstundenTagesRichtwert }) {
    // map to store fahrdienst hours per day

    const lastDayOfMonth = dateUtils.getLastDayOfMonth(currentWorksheetDate.getFullYear(), currentWorksheetDate.getMonth());

    let today = new Date();
    let month = today.getMonth();
    let year = today.getFullYear();

    const isInCurrentMonth = this.isDateOfCurrentMonth(month, year, currentWorksheetDate);
    let dateLastConsideredDay = isInCurrentMonth ? currentWorksheetDate.getDate() : lastDayOfMonth.getDate();
    let firstOfMonth = new Date(currentWorksheetDate.getFullYear(), currentWorksheetDate.getMonth(), 1);
    console.debug(`calculate stats for 1.-${dateLastConsideredDay}.${currentWorksheetDate.getMonth() + 1}`);

    let { applicableHoursPerDayInCurrentMonth, itemsPerDay } = this.calculateFahrdienstHour(
      absences,
      firstOfMonth,
      isInCurrentMonth ? currentWorksheetDate : lastDayOfMonth,
      zueri9Clients,
      zueri9Reasons
    );

    // Calculate daily differences and sum them up on a daily base
    const deltaSummedUpByDay = {};
    let deltaSum = 0;

    const fahrdienstStundenSummedUpByDay = {};
    let fahrdienstStundenSum = 0;

    [...Array(dateLastConsideredDay)]
      .map((_, i) => i + 1)
      .forEach((day) => {
        fahrdienstStundenSum += applicableHoursPerDayInCurrentMonth[day] || 0;
        fahrdienstStundenSummedUpByDay[day] = fahrdienstStundenSum;

        deltaSum += zueri9FahrstundenTagesRichtwert - (applicableHoursPerDayInCurrentMonth[day] || 0);
        deltaSummedUpByDay[day] = deltaSum;
      });

    // calculate all fahrdienst hours per day and summarize it
    let hoursAtContextDate = 0;
    let day = dateLastConsideredDay;
    if (isInCurrentMonth) {
      // when the statistics is displayed for the current month, use the figures of the display date  (not the current date)
      day = currentWorksheetDate.getDate();
      hoursAtContextDate = fahrdienstStundenSummedUpByDay[day];
    } else {
      day = fahrdienstStundenSummedUpByDay.length;
      hoursAtContextDate = fahrdienstStundenSummedUpByDay[day];
    }

    let delta = -utils.round(deltaSum, 2);

    let htmlFahrstundenAtCalculatedDate = `
       <span style="color: #77737a">Fahrstunden: </span>
       <span style="color: #77737a">${utils.round(fahrdienstStundenSum, 2)} h</span>`;
    let htmlDeltaMonthToDate =
      `
      <span style="color: #77737a">Abweichung vom SOLL: </span>` + //(1.${currentWorksheetDate.getMonth() + 1}.-${currentDateFormatted})
      (delta >= 0
        ? `<span style="color: #e32929">${delta} h über dem Monats-SOLL</span>`
        : `<span style="color: #e32929">${-delta} h unter dem Monats-SOLL</span>`);

    return {
      htmlFahrstundenAtCalculatedDate,
      htmlDeltaMonthToDate,
      calculatedForDate: currentWorksheetDate,
      applicableHoursPerDayInCurrentMonth,
      hoursAtContextDate,
      monthTotal: utils.round(fahrdienstStundenSum, 2),
      runningTotals: deltaSummedUpByDay,
      delta,
      itemsPerDay,
    };
  },
  /**
   * Checks whether the date object is of the given month/year.
   * @param month
   * @param year
   * @param date
   * @returns {boolean}
   */
  isDateOfCurrentMonth(month, year, date) {
    return date.getMonth && date.getMonth() === month && date.getFullYear() === year;
  },
  isBetweenIncludingEnd(date, fromDate, toDate) {
    //console.debug('check if date', date, `>=${fromDate} <= ${toDate}`);

    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    return year === fromDate.getFullYear() && month === fromDate.getMonth() && day >= fromDate.getDate() && day <= toDate.getDate();
  },
  /**
   *
   * @param {string} dutyFrom
   * @param {string} dutyUntil
   * @return {string}
   */
  calculateTimeDifferenceDecimal(dutyFrom, dutyUntil) {
    if (dutyFrom && dutyUntil) {
      const fromTime = Date.parse(dutyFrom);
      let untilTime = Date.parse(dutyUntil);
      if (untilTime < fromTime) untilTime += DAY_IN_MS; //Add 24h if until time is in the morning (e.g. 1am)
      const diffInMilliseconds = untilTime - fromTime;
      const diffInHours = diffInMilliseconds / HOURS_IN_MS; // convert milliseconds to hours
      // return diffInHours;
      return diffInHours.toFixed(2);
    }

    return '';
  },

  createDutySelectionFromHandmadeDutyParts(sendItem, isCreatedByZueri9 = false) {
    console.debug(`createDutySelectionFromHandmadeDutyParts isCreatedByZueri9=${isCreatedByZueri9}`, sendItem);

    let selectedDutyParts;
    if (isCreatedByZueri9) {
      selectedDutyParts = sendItem.z9DutyFrom
        ? [
            {
              course: sendItem.z9Course,
              line: sendItem.z9Line,
              fromMinutesAfterMidnight: dateUtils.getMinutesAfterMidnightFromTimeIsoString(sendItem.z9DutyFrom),
              toMinutesAfterMidnight: dateUtils.getMinutesAfterMidnightFromTimeIsoString(sendItem.z9DutyUntil),
              fromLoc: sendItem.z9DutyFromLoc,
              toLoc: sendItem.z9DutyUntilLoc,
            },
          ]
        : [];
    } else {
      selectedDutyParts = sendItem.dutyFrom
        ? [
            {
              course: sendItem.course,
              line: sendItem.line,
              fromMinutesAfterMidnight: dateUtils.getMinutesAfterMidnightFromTimeIsoString(sendItem.dutyFrom),
              toMinutesAfterMidnight: dateUtils.getMinutesAfterMidnightFromTimeIsoString(sendItem.dutyUntil),
              fromLoc: sendItem.dutyFromLoc,
              toLoc: sendItem.dutyUntilLoc,
            },
          ]
        : [];
    }
    if (!isCreatedByZueri9 && sendItem.dutyFrom2) {
      selectedDutyParts.push({
        course: sendItem.course2,
        line: sendItem.line2,
        fromMinutesAfterMidnight: dateUtils.getMinutesAfterMidnightFromTimeIsoString(sendItem.dutyFrom2),
        toMinutesAfterMidnight: dateUtils.getMinutesAfterMidnightFromTimeIsoString(sendItem.dutyUntil2),
        fromLoc: sendItem.dutyFromLoc2,
        toLoc: sendItem.dutyUntilLoc2,
      });
    }

    const dutySelection = {
      selectedDutyParts,
      dutyDetailsDateOfSelection: new Date().toISOString(),
    };
    return dutySelection;
  },
};
